import AvatarStack from "@/components/AvatarStack/AvatarStack"
import { useSessionStore } from "@/store/sessionStore"
import { selectCurrentDoneKey, selectDoneCount } from "@/store/selectors"
import type { FamilyNumber } from "@/theme/colors"
import styles from "@/styles/components/DoneRoster.module.css"

/*
 * Who has finished this beat.
 *
 * The multiplayer rule made visible: the facilitator does not move the room on
 * until the room is done, and this is how they know. Dimmed means the seat is
 * taken but the work is not — a distinction the count alone loses.
 *
 * `donePhase` is a key, not a boolean, so nothing is ever reset: a participant
 * who finished the previous phase simply no longer matches the current key and
 * shows as still working. That is why there is no "reset everyone" call.
 */

const STILL_WORKING = "still working"
const DONE = "done"

interface DoneRosterProps {
  /** Overrides "N of M done" — the Cards round says "picked", not "done". */
  label?: (done: number, total: number) => string
}

export default function DoneRoster({ label }: DoneRosterProps) {
  const participants = useSessionStore((s) => s.participants)
  const doneKey = useSessionStore(selectCurrentDoneKey)
  const doneCount = useSessionStore(selectDoneCount)

  if (participants.length === 0) return null

  const pending = participants.filter((p) => p.donePhase !== doneKey).map((p) => p.id)

  return (
    <div className={styles.roster}>
      <AvatarStack
        people={participants.map((participant, index) => ({
          ...participant,
          family: ((index % 7) + 1) as FamilyNumber,
        }))}
        dimmedIds={pending}
        suffixFor={(person) => (pending.includes(person.id) ? STILL_WORKING : DONE)}
      />
      <span className={styles.count}>
        {label
          ? label(doneCount, participants.length)
          : `${doneCount} of ${participants.length} done`}
      </span>
    </div>
  )
}
